/* eslint-disable jira/ff/unsafe-no-exposure */
/* eslint-disable jira/deprecations/ban-identifiers */
import React, { useCallback } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { HeadingItem, Section } from '@atlaskit/menu';

import { useTopMenus } from '@atlassian/jira-navigation-apps-common/src/controllers/top-menus/index.tsx';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { standardToPremiumPubTouchpointQuery } from '@atlassian/jira-relay/src/__generated__/standardToPremiumPubTouchpointQuery.graphql.tsx';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { EditionAwarenessStandardPubTouchpoint } from '@atlassian/edition-awareness-standard-pub-touchpoint';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useAnalyticsEvents,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	INLINE_DIALOG,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useIsEditionAwarenessPillDismissed } from '../../services/is-ea-pill-dismissed/index.tsx';
import { useIsEligibleForUpsellEa } from '../../services/is-eligible-for-upsell-ea/index.tsx';
import { StandardToPremiumModalEntrypointTrigger } from '../modal/trigger.tsx';
import { getHamsKey } from '../../utils/index.tsx';
import { useProductAndEdition } from '../../services/use-product-and-edition/index.tsx';
import { buildLearnMoreLink } from '../../utils/buildLearnMoreLink.tsx';
import messages from './messages.tsx';

function StandardToPremiumPubTouchpointImpl() {
	const [, { off: closeMenu }] = useTopMenus(MENU_ID.PROFILE);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const isSiteAdmin = useIsSiteAdmin();
	const cloudId = useCloudId();
	const { product, edition } = useProductAndEdition();
	const [expConfig] = UNSAFE_noExposureExp('edition_awareness_upsell_jira_ab');

	const {
		hasFetched: hasFetchedDismissedEditionAwarenessPillState,
		isDismissed: hasDismissedEditionAwarenessPill,
	} = useIsEditionAwarenessPillDismissed();

	const handleOnClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'editionAwarenessStandardToPremiumPUB',
		);

		closeMenu();
	}, [closeMenu, createAnalyticsEvent]);

	/**
	 * We won't know for certain if the user is currently on a paid standard plan until we query for the entitlement
	 * However, we can know for certain if they are not on any type of standard plan
	 * If they are not on a standard plan, we can skip the query
	 */
	let isPossiblyPaidStandard = false;
	if (edition === 'standard') {
		if (product === 'jira') {
			isPossiblyPaidStandard = fg('edition_awareness_upsell_jira_fg');
		}
		if (product === 'jira-service-management') {
			isPossiblyPaidStandard = fg('edition_awareness_upsell_jsm_fg');
		}
	}

	const cohort = expConfig.get('cohort', 'not-enrolled');
	const enrolled = cohort !== 'not-enrolled';

	// basic eligibility conditions
	const isEligibleForExperiment =
		isPossiblyPaidStandard &&
		isSiteAdmin &&
		hasFetchedDismissedEditionAwarenessPillState &&
		hasDismissedEditionAwarenessPill &&
		enrolled;

	// Only call api if the user is eligible and enrolled in the experiment
	const shouldSkip = !isEligibleForExperiment;

	const data = useLazyLoadQuery<standardToPremiumPubTouchpointQuery>(
		graphql`
			query standardToPremiumPubTouchpointQuery(
				$cloudId: ID!
				$productKey: String!
				$shouldSkip: Boolean!
			) {
				tenantContexts(cloudIds: [$cloudId])
					@skip(if: $shouldSkip)
					@optIn(to: ["CcpAllUserUpgradeAndPay", "HamsAllUserUpgradeAndPay"]) {
					entitlementInfo(hamsProductKey: $productKey) {
						paidEntitlement: entitlement(where: { inTrialOrPreDunning: false }) {
							experienceCapabilities {
								changeOfferingV2(offeringName: "premium") {
									isAvailableToUser
									experienceUrl
								}
							}
							subscription {
								accountDetails {
									invoiceGroup {
										invoiceable
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			shouldSkip,
			productKey: getHamsKey(product ?? ''), // will be skipped regardless if product is undefined because isPossiblyPaidStandard will default to false
		},
	);

	const paidEntitlement = data?.tenantContexts?.[0]?.entitlementInfo?.paidEntitlement;
	const isInvoiceable = paidEntitlement?.subscription?.accountDetails?.invoiceGroup?.invoiceable;
	const canUpgrade = Boolean(
		paidEntitlement?.experienceCapabilities?.changeOfferingV2?.isAvailableToUser &&
			paidEntitlement?.experienceCapabilities?.changeOfferingV2?.experienceUrl,
	);

	if (!paidEntitlement || !isInvoiceable || !canUpgrade) {
		return null;
	}

	const experienceUrl =
		paidEntitlement.experienceCapabilities?.changeOfferingV2?.experienceUrl ?? undefined;

	const learnMoreUrl = buildLearnMoreLink(experienceUrl);

	if (cohort === 'treatment' && product) {
		return (
			<ContextualAnalyticsData
				sourceName="editionAwarenessStandardToPremiumPUB"
				sourceType={INLINE_DIALOG}
			>
				<Section hasSeparator>
					<HeadingItem>{formatMessage(messages.upgrade)}</HeadingItem>
					<StandardToPremiumModalEntrypointTrigger
						cloudId={cloudId}
						product={product}
						experienceUrl={experienceUrl}
						learnMoreUrl={learnMoreUrl}
						onClick={handleOnClick}
					>
						<EditionAwarenessStandardPubTouchpoint product={product} />
					</StandardToPremiumModalEntrypointTrigger>
				</Section>
				<FireScreenAnalytics
					attributes={{
						cohort,
						flagKey: 'edition_awareness_upsell_jira_ab',
					}}
				/>
			</ContextualAnalyticsData>
		);
	}

	return null;
}

function StandardToPremiumUpsellPubTouchpointImpl() {
	const [, { off: closeMenu }] = useTopMenus(MENU_ID.PROFILE);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const isSiteAdmin = useIsSiteAdmin();
	const cloudId = useCloudId();
	const { product, edition } = useProductAndEdition();
	const { isEligibleForUpsellEa, cohort } = useIsEligibleForUpsellEa({
		product,
		edition,
		isSiteAdmin,
	});

	const {
		hasFetched: hasFetchedDismissedEditionAwarenessPillState,
		isDismissed: hasDismissedEditionAwarenessPill,
	} = useIsEditionAwarenessPillDismissed();

	const handleOnClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'editionAwarenessStandardToPremiumPUB',
		);

		closeMenu();
	}, [closeMenu, createAnalyticsEvent]);

	let isPaidStandard = false;
	if (edition === 'standard') {
		if (product === 'jira') {
			isPaidStandard = fg('edition_awareness_upsell_jira_fg');
		}
		if (product === 'jira-service-management') {
			isPaidStandard = fg('edition_awareness_upsell_jsm_fg');
		}
	}

	const shouldShowTouchpoint =
		isEligibleForUpsellEa &&
		cohort === 'treatment' &&
		isPaidStandard &&
		hasFetchedDismissedEditionAwarenessPillState &&
		hasDismissedEditionAwarenessPill &&
		product;

	if (shouldShowTouchpoint) {
		return (
			<ContextualAnalyticsData
				sourceName="editionAwarenessStandardToPremiumPUB"
				sourceType={INLINE_DIALOG}
			>
				<Section hasSeparator>
					<HeadingItem>{formatMessage(messages.upgrade)}</HeadingItem>
					<StandardToPremiumModalEntrypointTrigger
						cloudId={cloudId}
						product={product}
						onClick={handleOnClick}
					>
						<EditionAwarenessStandardPubTouchpoint product={product} />
					</StandardToPremiumModalEntrypointTrigger>
				</Section>
				<FireScreenAnalytics
					attributes={{
						cohort,
						flagKey: 'edition_awareness_upsell_jira_ab',
					}}
				/>
			</ContextualAnalyticsData>
		);
	}

	return null;
}

function StandardToPremiumPubTouchpointWithErrorBoundary() {
	return (
		<JSErrorBoundary
			id="editionAwarenessStandardToPremiumPubTouchpoint"
			packageName="jiraEditionAwarenessForStandardToPremium"
			fallback="unmount"
			teamName="luna-growth"
		>
			{fg('edition_awareness_upsell_custom_ea_implementation') ? (
				<StandardToPremiumUpsellPubTouchpointImpl />
			) : (
				<StandardToPremiumPubTouchpointImpl />
			)}
		</JSErrorBoundary>
	);
}

export const StandardToPremiumPubTouchpoint = componentWithFG(
	'edition_awareness_upsell_jira_ab_fg',
	StandardToPremiumPubTouchpointWithErrorBoundary,
	() => <></>,
);
